import React from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {ForecastQueryRenderer} from '../../../../ForecastQueryRenderer';
import InlineLoader from '../inline-loader/inline_loader';
import {RightDrawer} from '../right-drawer/RightDrawer';
import {ScheduleDrawerContent} from './ScheduleDrawerContent';
import {SCHEDULING_MESSAGE} from './schedulingMessages';

const query = graphql`
	query ScheduleDrawerQuery($consumer: String!) {
		viewer {
			id
			actualPersonId
			scheduleSet(consumer: $consumer) {
				...ScheduleDrawerContent_scheduleSet
			}
		}
	}
`;

type ScheduleDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	// The consumer's own name, already translated. It is the only consumer word the drawer shows.
	name: string;
	// The key the consumer's schedules are stored under in the scheduling service.
	consumer: string;
};

// One drawer for any consumer that runs on a cadence. It holds no permission logic: the consumer renders it
// only for people allowed to schedule. It loads only while open, and loads afresh each time it opens.
export const ScheduleDrawer = ({isOpen, onClose, name, consumer}: ScheduleDrawerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<RightDrawer
			isOpen={isOpen}
			title={formatMessage({id: SCHEDULING_MESSAGE.TITLE}, {name})}
			icon={'clock'}
			closeLabel={formatMessage({id: 'common.close'})}
			onClose={onClose}
			data-cy={'schedule-drawer'}
		>
			<ForecastQueryRenderer
				query={query}
				variables={{consumer}}
				fetchPolicy={'network-only'}
				customLoader={() => <InlineLoader />}
				render={(relayProps: {viewer?: {scheduleSet?: unknown}}, retry: () => void) =>
					relayProps.viewer?.scheduleSet ? (
						<ScheduleDrawerContent
							scheduleSet={relayProps.viewer.scheduleSet as never}
							name={name}
							onReload={retry}
						/>
					) : null
				}
			/>
		</RightDrawer>
	);
};
